import { useCallback, useEffect, useRef, useState } from "react";
import {
  Eye,
  EyeOff,
  History,
  Lock,
  Save,
  Settings as SettingsIcon,
  ShieldCheck,
  User,
  X,
} from "lucide-react";
import API, { fetchRecentSessions } from "../services/api";
import AppPageLoader from "../components/AppPageLoader";
import IpAuditModal from "../components/IpAuditModal";
import { getCurrentUser, setCurrentUserCache } from "../services/sessionCache";
import { useToast } from "../components/Toast";
import { formatAppDateTime } from "../utils/dateFormat";
import "./Settings.css";

const SESSION_AUTH_METHOD_LABELS = {
  local: "Email",
  google: "Google",
  x: "Twitter / X",
};
const MAX_AVATAR_BYTES = 2 * 1024 * 1024;
const SUPPORTED_AVATAR_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

function getSessionText(value, fallback = "Unavailable") {
  const normalizedValue = typeof value === "string" ? value.trim() : value;
  return normalizedValue ? normalizedValue : fallback;
}

function getSessionAuthMethodLabel(authMethod) {
  const normalizedMethod = String(authMethod || "")
    .trim()
    .toLowerCase();
  return SESSION_AUTH_METHOD_LABELS[normalizedMethod] || getSessionText(authMethod);
}

function formatRelativeSessionTime(value) {
  if (!value) return "Unavailable";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unavailable";

  const diffMs = Math.max(0, Date.now() - date.getTime());
  const seconds = Math.max(1, Math.floor(diffMs / 1000));
  const minutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(diffMs / 3600000);
  const days = Math.floor(diffMs / 86400000);

  if (seconds < 60) return `${seconds}s ago`;
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${Math.max(1, days)}d ago`;
}

function getAvatarInitials(name, email) {
  const base = String(name || email || "User").trim();
  if (!base) return "U";

  const parts = base.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase() || "U";
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Failed to read image file."));
    reader.readAsDataURL(file);
  });
}

function extractUserPayload(data) {
  return data?.user || data?.data || data || {};
}

export default function Settings() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [profileBusy, setProfileBusy] = useState(false);
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [avatarDraftUrl, setAvatarDraftUrl] = useState("");
  const [pendingAvatarDataUrl, setPendingAvatarDataUrl] = useState("");
  const [avatarMarkedForRemoval, setAvatarMarkedForRemoval] = useState(false);
  const [profileSnapshot, setProfileSnapshot] = useState({
    name: "",
    avatarUrl: "",
  });
  const [authProvider, setAuthProvider] = useState("local");
  const [canSetPasswordWithoutCurrent, setCanSetPasswordWithoutCurrent] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordChangedAt, setPasswordChangedAt] = useState(null);
  const [visiblePasswords, setVisiblePasswords] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });
  const [recentSessions, setRecentSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [sessionsError, setSessionsError] = useState("");
  const [selectedAuditIp, setSelectedAuditIp] = useState("");
  const avatarInputRef = useRef(null);
  const userStateRef = useRef({
    name: "",
    email: "",
    authProvider: "local",
    passwordChangedAt: null,
    canSetPasswordWithoutCurrent: false,
    avatarUrl: "",
  });

  userStateRef.current = {
    name,
    email,
    authProvider,
    passwordChangedAt,
    canSetPasswordWithoutCurrent,
    avatarUrl,
  };

  const syncUserState = useCallback((incomingUser = {}, overrides = {}) => {
    const currentUserState = userStateRef.current;
    const nextName = String(
      overrides.displayName ?? incomingUser.displayName ?? currentUserState.name ?? "",
    ).trim();
    const nextEmail = String(
      overrides.email ?? incomingUser.email ?? currentUserState.email ?? "",
    ).trim();
    const nextAuthProvider =
      String(
        overrides.authProvider ??
          incomingUser.authProvider ??
          currentUserState.authProvider ??
          "local",
      ).trim() || "local";
    const nextPasswordChangedAt =
      overrides.passwordChangedAt !== undefined
        ? overrides.passwordChangedAt
        : (incomingUser.passwordChangedAt ?? currentUserState.passwordChangedAt ?? null);
    const nextCanSetPasswordWithoutCurrent =
      overrides.canSetPasswordWithoutCurrent !== undefined
        ? overrides.canSetPasswordWithoutCurrent
        : (incomingUser.canSetPasswordWithoutCurrent ??
          currentUserState.canSetPasswordWithoutCurrent ??
          false);
    const nextAvatarUrl =
      overrides.avatarUrl !== undefined
        ? overrides.avatarUrl
        : (incomingUser.avatarUrl ?? currentUserState.avatarUrl ?? "");

    const mergedUser = {
      ...incomingUser,
      displayName: nextName,
      email: nextEmail,
      authProvider: nextAuthProvider,
      passwordChangedAt: nextPasswordChangedAt,
      canSetPasswordWithoutCurrent: Boolean(nextCanSetPasswordWithoutCurrent),
      avatarUrl: nextAvatarUrl || "",
    };

    setName(nextName);
    setEmail(nextEmail);
    setAuthProvider(nextAuthProvider);
    setPasswordChangedAt(nextPasswordChangedAt);
    setCanSetPasswordWithoutCurrent(Boolean(nextCanSetPasswordWithoutCurrent));
    setAvatarUrl(nextAvatarUrl || "");
    setAvatarDraftUrl(nextAvatarUrl || "");
    setPendingAvatarDataUrl("");
    setAvatarMarkedForRemoval(false);
    setProfileSnapshot({
      name: nextName,
      avatarUrl: nextAvatarUrl || "",
    });
    setCurrentUserCache(mergedUser);
    return mergedUser;
  }, []);

  useEffect(() => {
    let isActive = true;

    const loadProfile = async () => {
      try {
        const user = await getCurrentUser({ force: true });
        if (!isActive) return;
        syncUserState(user || {});
      } catch (err) {
        if (!isActive) return;
        toast.error("Failed to load settings.");
      } finally {
        if (isActive) setLoading(false);
      }
    };

    loadProfile();

    return () => {
      isActive = false;
    };
  }, [syncUserState, toast]);

  useEffect(() => {
    let isActive = true;

    const loadRecentSessions = async () => {
      setSessionsLoading(true);
      setSessionsError("");

      try {
        const response = await fetchRecentSessions();
        if (!isActive) return;
        setRecentSessions(Array.isArray(response.data?.sessions) ? response.data.sessions : []);
      } catch (err) {
        if (!isActive) return;
        setRecentSessions([]);
        setSessionsError(
          err.response?.data?.message ||
            err.response?.data?.msg ||
            "Failed to load recent sessions.",
        );
      } finally {
        if (isActive) setSessionsLoading(false);
      }
    };

    loadRecentSessions();

    return () => {
      isActive = false;
    };
  }, []);

  const togglePasswordVisibility = (field) => {
    setVisiblePasswords((current) => ({
      ...current,
      [field]: !current[field],
    }));
  };

  const handlePasswordChange = (field, value) => {
    setPasswordForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleSaveProfile = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error("Name is required.");
      return;
    }

    if (!hasPendingProfileChanges) {
      return;
    }

    setProfileBusy(true);
    try {
      let mergedUser = {};

      if (hasPendingNameChange) {
        const response = await API.put("/users/profile", {
          displayName: trimmedName,
        });
        mergedUser = {
          ...mergedUser,
          ...extractUserPayload(response.data),
        };
      }

      if (pendingAvatarDataUrl) {
        const response = await API.post("/users/avatar", { avatarDataUrl: pendingAvatarDataUrl });
        mergedUser = {
          ...mergedUser,
          ...extractUserPayload(response.data),
        };
      } else if (avatarMarkedForRemoval && profileSnapshot.avatarUrl) {
        const response = await API.delete("/users/avatar");
        mergedUser = {
          ...mergedUser,
          ...extractUserPayload(response.data),
          avatarUrl: "",
        };
      }

      syncUserState(mergedUser, {
        displayName: mergedUser.displayName || trimmedName,
        avatarUrl:
          mergedUser.avatarUrl !== undefined
            ? mergedUser.avatarUrl
            : avatarMarkedForRemoval
              ? ""
              : pendingAvatarDataUrl
                ? avatarDraftUrl
                : avatarUrl,
      });

      toast.success("Profile updated.");
    } catch (err) {
      toast.error(
        err.response?.data?.msg || err.response?.data?.message || "Failed to update profile.",
      );
    } finally {
      setProfileBusy(false);
    }
  };

  const handleSelectAvatar = () => {
    if (profileBusy) return;
    avatarInputRef.current?.click();
  };

  const handleAvatarChange = async (event) => {
    const input = event.target;
    const file = input.files?.[0];

    if (!file) return;

    if (!SUPPORTED_AVATAR_TYPES.has(String(file.type || "").toLowerCase())) {
      input.value = "";
      toast.error("Use a JPG, PNG, or WEBP image.");
      return;
    }

    if (file.size > MAX_AVATAR_BYTES) {
      input.value = "";
      toast.error("Avatar image must be 2MB or smaller.");
      return;
    }

    try {
      const avatarDataUrl = await readFileAsDataUrl(file);
      setAvatarDraftUrl(avatarDataUrl);
      setPendingAvatarDataUrl(avatarDataUrl);
      setAvatarMarkedForRemoval(false);
    } catch (err) {
      toast.error(err.message || "Failed to read avatar image.");
    } finally {
      input.value = "";
    }
  };

  const handleRemoveAvatar = () => {
    if (profileBusy) return;

    setAvatarDraftUrl("");
    setPendingAvatarDataUrl("");
    setAvatarMarkedForRemoval(Boolean(profileSnapshot.avatarUrl));
  };

  const handleUpdatePassword = async () => {
    if (
      (!canSetPasswordWithoutCurrent && !passwordForm.currentPassword) ||
      !passwordForm.newPassword ||
      !passwordForm.confirmPassword
    ) {
      toast.error(
        canSetPasswordWithoutCurrent
          ? "New password fields are required."
          : "All password fields are required.",
      );
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      toast.error("New password must be at least 6 characters.");
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error("Confirm password does not match.");
      return;
    }

    setPasswordBusy(true);
    try {
      const payload = {
        newPassword: passwordForm.newPassword,
      };
      if (!canSetPasswordWithoutCurrent) {
        payload.currentPassword = passwordForm.currentPassword;
      }

      const response = await API.post("/users/change-password", payload);
      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      const nextPasswordChangedAt =
        response.data?.passwordChangedAt || response.data?.user?.passwordChangedAt || null;
      const nextUser = response.data?.user || null;
      if (nextUser) {
        syncUserState(nextUser, { passwordChangedAt: nextPasswordChangedAt });
      }
      if (nextPasswordChangedAt) setPasswordChangedAt(nextPasswordChangedAt);
      setCanSetPasswordWithoutCurrent(false);
      toast.success(response.data?.msg || response.data?.message || "Password updated.");
    } catch (err) {
      toast.error(
        err.response?.data?.msg || err.response?.data?.message || "Failed to update password.",
      );
    } finally {
      setPasswordBusy(false);
    }
  };

  const normalizedAuthProvider = String(authProvider || "local")
    .trim()
    .toLowerCase();
  const showProviderChip = normalizedAuthProvider === "google" || normalizedAuthProvider === "x";
  const socialProviderLabel =
    normalizedAuthProvider === "google"
      ? "Google"
      : normalizedAuthProvider === "x"
        ? "Twitter / X"
        : "";
  const passwordSectionDescription = canSetPasswordWithoutCurrent
    ? `Your account signs in with ${socialProviderLabel || "a social account"}. Set a password if you also want email login.`
    : "Change your password without affecting the rest of your account settings.";
  const passwordActionLabel = canSetPasswordWithoutCurrent ? "Set Password" : "Update Password";
  const trimmedName = name.trim();
  const avatarInitials = getAvatarInitials(name, email);
  const displayAvatarUrl = avatarMarkedForRemoval ? "" : avatarDraftUrl || avatarUrl || "";
  const hasPendingNameChange = trimmedName !== profileSnapshot.name;
  const hasPendingAvatarChange = Boolean(pendingAvatarDataUrl) || avatarMarkedForRemoval;
  const hasPendingProfileChanges = hasPendingNameChange || hasPendingAvatarChange;
  if (loading) {
    return (
      <div className="settings-page settings-page-loading">
        <AppPageLoader
          label="Loading Settings"
          detail="Preparing your profile and security controls..."
        />
      </div>
    );
  }

  return (
    <div className="settings-page">
      <div className="settings-header">
        <div>
          <h1>
            <SettingsIcon className="settings-header-icon" />
            <span>Settings</span>
          </h1>
          <p>Update your display name and security settings from one focused account page.</p>
        </div>
      </div>

      <div className="settings-stack">
        <section className="settings-card">
          <div className="settings-card-heading">
            <div className="settings-card-copy">
              <div className="settings-card-title">
                <User />
                <span>Profile</span>
                {showProviderChip && (
                  <span
                    className={`settings-provider-chip settings-provider-chip-${normalizedAuthProvider}`}
                  >
                    {normalizedAuthProvider === "google" ? (
                      <svg
                        className="settings-provider-chip-icon"
                        viewBox="-3 0 262 262"
                        xmlns="http://www.w3.org/2000/svg"
                        preserveAspectRatio="xMidYMid"
                        aria-hidden="true"
                      >
                        <path
                          d="M255.878 133.451c0-10.734-.871-18.567-2.756-26.69H130.55v48.448h71.947c-1.45 12.04-9.283 30.172-26.69 42.356l-.244 1.622 38.755 30.023 2.685.268c24.659-22.774 38.875-56.282 38.875-96.027"
                          fill="currentColor"
                        />
                        <path
                          d="M130.55 261.1c35.248 0 64.839-11.605 86.453-31.622l-41.196-31.913c-11.024 7.688-25.82 13.055-45.257 13.055-34.523 0-63.824-22.773-74.269-54.25l-1.531.13-40.298 31.187-.527 1.465C35.393 231.798 79.49 261.1 130.55 261.1"
                          fill="currentColor"
                        />
                        <path
                          d="M56.281 156.37c-2.756-8.123-4.351-16.827-4.351-25.82 0-8.994 1.595-17.697 4.206-25.82l-.073-1.73L15.26 71.312l-1.335.635C5.077 89.644 0 109.517 0 130.55s5.077 40.905 13.925 58.602l42.356-32.782"
                          fill="currentColor"
                        />
                        <path
                          d="M130.55 50.479c24.514 0 41.05 10.589 50.479 19.438l36.844-35.974C195.245 12.91 165.798 0 130.55 0 79.49 0 35.393 29.301 13.925 71.947l42.211 32.783c10.59-31.477 39.891-54.251 74.414-54.251"
                          fill="currentColor"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="settings-provider-chip-icon"
                        viewBox="0 0 300 271"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                        focusable="false"
                      >
                        <path
                          fill="currentColor"
                          d="m236 0h46l-101 115 118 156h-92.6l-72.5-94.8-83 94.8h-46l107-123-113-148h94.9l65.5 86.6zm-16.1 244h25.5l-165-218h-27.4z"
                        />
                      </svg>
                    )}
                    <span>{socialProviderLabel}</span>
                  </span>
                )}
              </div>
              <p>Control how your account identity appears across the workspace.</p>
            </div>
          </div>

          <div className="settings-profile-grid">
            <div className="settings-avatar-panel">
              <div className="settings-avatar-shell">
                <div className="settings-avatar-frame">
                  <button
                    type="button"
                    className="settings-avatar-preview settings-avatar-trigger"
                    onClick={handleSelectAvatar}
                    disabled={profileBusy}
                    aria-label={displayAvatarUrl ? "Change profile photo" : "Upload profile photo"}
                  >
                    {displayAvatarUrl ? (
                      <img
                        src={displayAvatarUrl}
                        alt="Profile avatar"
                        className="settings-avatar-image"
                      />
                    ) : (
                      <div className="settings-avatar-fallback" aria-hidden="true">
                        <span>{avatarInitials}</span>
                      </div>
                    )}
                    <span className="settings-avatar-verified-badge" aria-hidden="true">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                      >
                        <path
                          fill="currentColor"
                          d="M8.15 21.75L6.7 19.3l-2.75-.6q-.375-.075-.6-.387t-.175-.688L3.45 14.8l-1.875-2.15q-.25-.275-.25-.65t.25-.65L3.45 9.2l-.275-2.825q-.05-.375.175-.688t.6-.387l2.75-.6l1.45-2.45q.2-.325.55-.438t.7.038l2.6 1.1l2.6-1.1q.35-.15.7-.038t.55.438L17.3 4.7l2.75.6q.375.075.6.388t.175.687L20.55 9.2l1.875 2.15q.25.275.25.65t-.25.65L20.55 14.8l.275 2.825q.05.375-.175.688t-.6.387l-2.75.6l-1.45 2.45q-.2.325-.55.438t-.7-.038l-2.6-1.1l-2.6 1.1q-.35.15-.7.038t-.55-.438"
                        />
                        <path
                          fill="#ffffff"
                          d="M10.95 12.7L9.5 11.275Q9.225 11 8.813 11t-.713.3q-.275.275-.275.7t.275.7l2.15 2.15q.3.3.7.3t.7-.3l4.25-4.25q.3-.3.287-.7t-.287-.7q-.3-.3-.712-.312t-.713.287z"
                        />
                      </svg>
                    </span>
                  </button>

                  {displayAvatarUrl && (
                    <button
                      type="button"
                      className="settings-avatar-remove-button"
                      onClick={handleRemoveAvatar}
                      disabled={profileBusy}
                      aria-label="Remove profile photo"
                    >
                      <X size={12} />
                    </button>
                  )}

                  <input
                    ref={avatarInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="settings-avatar-input"
                    onChange={handleAvatarChange}
                    disabled={profileBusy}
                  />
                </div>

                <p className="settings-avatar-helper">JPG, PNG, WEBP up to 2MB</p>
              </div>
            </div>

            <div className="settings-field-group">
              <label className="settings-label">Name</label>
              <input
                type="text"
                value={name}
                onChange={(event) => setName(event.target.value.slice(0, 100))}
                placeholder="Enter your name"
                className="settings-input"
                maxLength="100"
              />
            </div>

            <div className="settings-field-group">
              <label className="settings-label">Email</label>
              <input
                type="text"
                value={email}
                readOnly
                className="settings-input settings-input-readonly"
              />
            </div>

            <div className="settings-actions settings-profile-actions">
              <button
                type="button"
                className="settings-btn settings-btn-primary"
                onClick={handleSaveProfile}
                disabled={profileBusy || !trimmedName || !hasPendingProfileChanges}
              >
                <Save size={15} />
                {profileBusy ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </section>

        <section className="settings-card">
          <div className="settings-card-heading">
            <div className="settings-card-copy">
              <div className="settings-card-title">
                <ShieldCheck />
                <span>Security</span>
              </div>
              <p>
                {passwordSectionDescription}
                {passwordChangedAt && (
                  <span
                    style={{ display: "block", marginTop: "6px", fontSize: "0.8rem", opacity: 0.6 }}
                  >
                    Last changed: {formatAppDateTime(passwordChangedAt)}
                  </span>
                )}
              </p>
            </div>
          </div>

          <div className="settings-password-grid">
            {!canSetPasswordWithoutCurrent && (
              <div className="settings-field-group">
                <label className="settings-label">
                  <Lock size={13} />
                  Current Password
                </label>
                <div className="settings-password-wrap">
                  <input
                    type={visiblePasswords.currentPassword ? "text" : "password"}
                    value={passwordForm.currentPassword}
                    onChange={(event) =>
                      handlePasswordChange("currentPassword", event.target.value)
                    }
                    placeholder="Enter current password"
                    className="settings-input settings-password-input"
                  />
                  <button
                    type="button"
                    className="settings-password-toggle"
                    onClick={() => togglePasswordVisibility("currentPassword")}
                    aria-label={
                      visiblePasswords.currentPassword
                        ? "Hide current password"
                        : "Show current password"
                    }
                  >
                    {visiblePasswords.currentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
            )}

            <div className="settings-field-group">
              <label className="settings-label">
                <Lock size={13} />
                New Password
              </label>
              <div className="settings-password-wrap">
                <input
                  type={visiblePasswords.newPassword ? "text" : "password"}
                  value={passwordForm.newPassword}
                  onChange={(event) => handlePasswordChange("newPassword", event.target.value)}
                  placeholder={
                    canSetPasswordWithoutCurrent ? "Create a password" : "Enter new password"
                  }
                  className="settings-input settings-password-input"
                />
                <button
                  type="button"
                  className="settings-password-toggle"
                  onClick={() => togglePasswordVisibility("newPassword")}
                  aria-label={
                    visiblePasswords.newPassword ? "Hide new password" : "Show new password"
                  }
                >
                  {visiblePasswords.newPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="settings-field-group">
              <label className="settings-label">
                <Lock size={13} />
                Confirm New Password
              </label>
              <div className="settings-password-wrap">
                <input
                  type={visiblePasswords.confirmPassword ? "text" : "password"}
                  value={passwordForm.confirmPassword}
                  onChange={(event) => handlePasswordChange("confirmPassword", event.target.value)}
                  placeholder="Confirm new password"
                  className="settings-input settings-password-input"
                />
                <button
                  type="button"
                  className="settings-password-toggle"
                  onClick={() => togglePasswordVisibility("confirmPassword")}
                  aria-label={
                    visiblePasswords.confirmPassword
                      ? "Hide confirm password"
                      : "Show confirm password"
                  }
                >
                  {visiblePasswords.confirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="settings-actions settings-password-actions">
              <button
                type="button"
                className="settings-btn settings-btn-primary"
                onClick={handleUpdatePassword}
                disabled={passwordBusy}
              >
                <Save size={15} />
                {passwordBusy
                  ? canSetPasswordWithoutCurrent
                    ? "Saving..."
                    : "Updating..."
                  : passwordActionLabel}
              </button>
            </div>
          </div>
        </section>

        <section className="settings-card">
          <div className="settings-card-heading">
            <div className="settings-card-copy">
              <div className="settings-card-title">
                <History />
                <span>Recent Sessions</span>
              </div>
              <p>Your last 5 successful sign-ins</p>
            </div>
          </div>

          {sessionsLoading ? (
            <div className="settings-sessions-list">
              {[1, 2, 3].map((i) => (
                <article key={i} className="settings-session-item settings-session-skeleton">
                  <div className="settings-session-top">
                    <span className="skel skel-index" />
                    <div className="settings-session-summary">
                      <div className="settings-session-primary">
                        <span className="skel skel-text skel-w-80" />
                        <span className="skel skel-text skel-w-50" />
                      </div>
                      <div className="settings-session-badges">
                        <span className="skel skel-badge" />
                        <span className="skel skel-badge skel-w-40" />
                      </div>
                    </div>
                    <span className="skel skel-text skel-w-60" />
                  </div>
                </article>
              ))}
            </div>
          ) : sessionsError ? (
            <div className="settings-empty settings-empty-error">{sessionsError}</div>
          ) : recentSessions.length === 0 ? (
            <div className="settings-empty">No recent sessions found.</div>
          ) : (
            <div className="settings-sessions-list">
              {recentSessions.map((session, index) => {
                const browser = getSessionText(session?.browser, "Unknown browser");
                const os = getSessionText(session?.os, "Unknown OS");
                const rawIpAddress =
                  typeof session?.ipAddress === "string" ? session.ipAddress.trim() : "";
                const ipAddress = rawIpAddress || "Unavailable";
                const normalizedAuthMethod = String(session?.authMethod || "")
                  .trim()
                  .toLowerCase();
                const authMethod = getSessionAuthMethodLabel(session?.authMethod);
                const userAgent = getSessionText(session?.userAgent);
                const relativeCreatedAt = formatRelativeSessionTime(session?.createdAt);

                return (
                  <article
                    key={session?.id || `${session?.createdAt || "session"}-${index}`}
                    className={`settings-session-item${index === 0 ? " settings-session-current" : ""}`}
                  >
                    {index === 0 && (
                      <span className="settings-session-current-tag">Current Session</span>
                    )}
                    <div className="settings-session-top">
                      <span className="settings-session-index">{index + 1}</span>
                      <div className="settings-session-summary">
                        <div className="settings-session-primary">
                          <span className="settings-session-browser">{browser}</span>
                          <span className="settings-session-separator" aria-hidden="true">
                            •
                          </span>
                          <span>{os}</span>
                        </div>

                        <div className="settings-session-badges">
                          {rawIpAddress ? (
                            <button
                              type="button"
                              className="settings-session-pill settings-session-pill-button settings-session-badge"
                              onClick={() => setSelectedAuditIp(rawIpAddress)}
                              title={`Inspect ${rawIpAddress}`}
                            >
                              {ipAddress}
                            </button>
                          ) : (
                            <span className="settings-session-pill settings-session-badge">
                              {ipAddress}
                            </span>
                          )}
                          <span
                            className={[
                              "settings-provider-chip",
                              "settings-session-badge",
                              normalizedAuthMethod && normalizedAuthMethod !== "local"
                                ? `settings-provider-chip-${normalizedAuthMethod}`
                                : "",
                            ]
                              .filter(Boolean)
                              .join(" ")}
                          >
                            {authMethod}
                          </span>
                        </div>
                      </div>
                      <div className="settings-session-time-primary">{relativeCreatedAt}</div>
                    </div>

                    <p className="settings-session-user-agent">{userAgent}</p>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </div>

      <IpAuditModal
        open={Boolean(selectedAuditIp)}
        ip={selectedAuditIp}
        onClose={() => setSelectedAuditIp("")}
      />
    </div>
  );
}
